import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { APP_VERSION } from "../lib/version";
import AboutModal from "../components/AboutModal";
import { useEntorno, RISE_GREEN } from "../components/RiseBanner";   // v101.159.0
import {
  Loader2,
  Mail,
  Lock,
  ArrowRight,
  KeyRound,
  ShieldCheck,
  Zap,
  Radio,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Login v3.0 · noCRM-inspired layout with Enterprise Design System colors
 * ═══════════════════════════════════════════════════════════════════════
 * Left  · Big brand panel (#0A3747) with yellow accent and friendly copy
 * Right · Clean white card with the login form (spacious, rounded)
 * ═══════════════════════════════════════════════════════════════════════
 */

const HIGHLIGHTS = [
  { icon: Radio,     title: "Órdenes en vivo",       desc: "Kanban visual y tablero de campo en tiempo real." },
  { icon: MapPin,    title: "GPS auditado",          desc: "Ubicación declarada vs. validada del técnico." },
  { icon: ShieldCheck, title: "Fiscalización rápida", desc: "NCs y control de calidad sin fricción." },
  { icon: Zap,       title: "Pre-facturación",       desc: "SLA + garantías consolidadas al instante." },
];

export default function Login() {
  const { user, login } = useAuth();
  const _rise = !!useEntorno()?.rise;   // v101.159.0 · antes de cualquier return (regla de hooks)
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const handle = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) return;
    setLoading(true);
    try {
      const result = await login(email, password, mfaRequired ? mfaCode : null);
      if (result.mfa_required) {
        setMfaRequired(true);
        toast.info("Ingresa el código de 6 dígitos de tu app autenticadora");
      } else {
        toast.success("¡Bienvenido a Alfa-Field Connect!");
        navigate("/dashboard");
      }
    } catch (err) {
      // v92.9.0 · Distinguir caída/reinicio del servidor de credenciales incorrectas
      if (!err?.response) toast.error("No hay conexión con el servidor. Intenta nuevamente en un minuto.");
      else if (err.response.status >= 500) toast.error(err.response.data?.detail || "El sistema se está reiniciando. Intenta nuevamente en un minuto.");
      else toast.error(err?.response?.data?.detail || "Credenciales incorrectas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ background: "var(--bg-app)" }}
      data-testid="login-page"
    >

      {/* ═════ LEFT · Brand panel (corporate blue) — logo protagonista ═════ */}
      <aside
        data-testid="login-hero"
        className="hidden lg:flex lg:w-[54%] relative overflow-hidden flex-col items-center justify-center p-12 xl:p-16"
        style={{ background: "#0A3747" }}
      >
        {/* Soft yellow spotlight behind logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="absolute pointer-events-none"
          style={{
            width: 640,
            height: 640,
            top: "42%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "radial-gradient(circle, rgba(224,230,33,0.16) 0%, rgba(224,230,33,0.06) 40%, transparent 70%)",
            filter: "blur(12px)",
          }}
        />
        {/* Dot pattern */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, #E0E621 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />

        {/* HERO LOGO — protagonista */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 flex flex-col items-center"
        >
          {/* Yellow ring frame */}
          <div
            className="relative rounded-[36px] p-2.5 mb-8"
            style={{
              background: "linear-gradient(145deg, rgba(224,230,33,0.35), rgba(224,230,33,0.08))",
              boxShadow:
                "0 30px 80px -20px rgba(224,230,33,0.45), 0 0 0 1px rgba(224,230,33,0.25), inset 0 0 24px rgba(224,230,33,0.15)",
            }}
          >
            <div
              className="rounded-[28px] p-6"
              style={{
                background: "linear-gradient(145deg, #FFFFFF 0%, #F1F5F9 100%)",
                boxShadow: "inset -6px -6px 14px rgba(255,255,255,0.9), inset 6px 6px 14px rgba(10,55,71,0.12)",
              }}
            >
              <img
                src="/logo-alfanet.png"
                alt="Alfanet"
                className="w-56 h-56 xl:w-64 xl:h-64 object-contain"
                draggable={false}
              />
            </div>
            {/* Corner accents */}
            <span className="absolute -top-1 -left-1 w-4 h-4 rounded-tl-xl" style={{ borderTop: "3px solid #E0E621", borderLeft: "3px solid #E0E621" }} />
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-tr-xl" style={{ borderTop: "3px solid #E0E621", borderRight: "3px solid #E0E621" }} />
            <span className="absolute -bottom-1 -left-1 w-4 h-4 rounded-bl-xl" style={{ borderBottom: "3px solid #E0E621", borderLeft: "3px solid #E0E621" }} />
            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-br-xl" style={{ borderBottom: "3px solid #E0E621", borderRight: "3px solid #E0E621" }} />
          </div>

          {/* Company tag */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
            style={{ background: "rgba(224,230,33,0.14)", border: "1px solid rgba(224,230,33,0.4)" }}
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#E0E621" }} />
            <span className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: "#E0E621" }}>
              Telealfacom · S.A.
            </span>
          </motion.div>

          {/* Product name */}
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-white font-bold text-4xl xl:text-5xl tracking-tight text-center"
          >
            Alfa-Field <span style={{ color: "#E0E621" }}>Connect</span>
          </motion.h1>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-white/70 text-base xl:text-lg text-center mt-3 max-w-md"
          >
            Orquestador de operaciones de campo en tiempo real.
          </motion.p>

          {/* Highlight badges — compactas horizontales */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.55 }}
            className="flex flex-wrap items-center justify-center gap-2 mt-8 max-w-lg"
          >
            {HIGHLIGHTS.map((h) => (
              <div
                key={h.title}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  backdropFilter: "blur(6px)",
                }}
              >
                <h.icon size={13} style={{ color: "#E0E621" }} strokeWidth={2.5} />
                <span className="text-white/85 text-[12px] font-medium">{h.title}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Bottom footer */}
        <div className="absolute bottom-8 left-12 right-12 z-10 flex items-center justify-between text-white/45 text-xs">
          <span>© {new Date().getFullYear()} Telealfacom · Todos los derechos reservados</span>
          <button
            onClick={() => setAboutOpen(true)}
            className="hover:text-white transition font-semibold"
            data-testid="login-about-link"
          >
            Acerca de la plataforma →
          </button>
        </div>
      </aside>

      {/* ═════ RIGHT · Login card ═════ */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile-only brand — con logo grande */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <div
              className="rounded-3xl p-2.5 mb-4"
              style={{
                background: "linear-gradient(145deg, rgba(224,230,33,0.35), rgba(224,230,33,0.08))",
                boxShadow: "0 20px 40px -12px rgba(224,230,33,0.4), 0 0 0 1px rgba(224,230,33,0.3)",
              }}
            >
              <div
                className="rounded-2xl p-4"
                style={{
                  background: "linear-gradient(145deg, #FFFFFF, #F1F5F9)",
                  boxShadow: "inset -4px -4px 10px rgba(255,255,255,0.9), inset 4px 4px 10px rgba(10,55,71,0.1)",
                }}
              >
                <img src="/logo-alfanet.png" alt="Alfanet" className="w-24 h-24 object-contain" draggable={false} />
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#6B7000" }}>
              Telealfacom · S.A.
            </div>
            <div className="font-bold text-xl mt-1" style={{ color: "#0A3747" }}>
              Alfa-Field <span style={{ color: "#C4CA12" }}>Connect</span>
            </div>
          </div>

          <div
            className="neu-card-lg p-8 lg:p-10 afc-appear"
          >
            {/* v101.159.0 · Ambiente de pruebas RISE: aviso grande en el login */}
            {_rise && (
              <div className="mb-5 rounded-xl px-4 py-3 text-white" style={{ background: RISE_GREEN }} data-testid="login-rise">
                <div className="text-2xl font-black tracking-widest">RISE</div>
                <div className="text-xs font-semibold">Ambiente de PRUEBAS · no es producción</div>
              </div>
            )}
            {/* Header */}
            <div className="mb-8">
              <div
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-4"
                style={{ background: "#FBFCE0", border: "1px solid #E9EE6E" }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#C4CA12" }} />
                <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "#6B7000" }}>
                  Inicia sesión
                </span>
              </div>
              <h2 className="text-3xl font-bold tracking-tight" style={{ color: "#0A3747" }}>
                ¡Hola de nuevo! 👋
              </h2>
              <p className="text-sm mt-1.5" style={{ color: "#64748B" }}>
                Ingresa a tu cuenta para continuar orquestando el campo.
              </p>
            </div>

            <form onSubmit={handle} className="space-y-5" data-testid="login-form">
              {/* Email */}
              <div>
                <label className="block text-[13px] font-semibold mb-1.5" style={{ color: "#0A3747" }}>
                  Correo electrónico
                </label>
                <div className="relative">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="neu-input pl-11"
                    placeholder="usuario@telealfacom.com"
                    required
                    autoComplete="email"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[13px] font-semibold" style={{ color: "#0A3747" }}>
                    Contraseña
                  </label>
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-[12px] font-medium hover:underline"
                    style={{ color: "#0A3747" }}
                    data-testid="forgot-password-link"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="neu-input pl-11"
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              {/* MFA */}
              {mfaRequired && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="rounded-xl p-4"
                  style={{ background: "#FEF3C7", border: "1.5px solid #FCD34D" }}
                  data-testid="mfa-field"
                >
                  <label className="block text-[13px] font-semibold mb-1.5" style={{ color: "#92400E" }}>
                    Código de verificación (2FA)
                  </label>
                  <div className="relative">
                    <KeyRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#D97706" }} />
                    <input
                      type="text"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                      className="w-full pl-11 pr-4 py-3 text-base tracking-widest rounded-lg outline-none font-mono"
                      style={{
                        background: "#FFFFFF",
                        border: "1.5px solid #FCD34D",
                        color: "#92400E",
                      }}
                      placeholder="123 456"
                      autoFocus
                      required
                      data-testid="login-mfa-input"
                    />
                  </div>
                  <div className="text-[11px] mt-2" style={{ color: "#92400E" }}>
                    Ingresa el código de Google Authenticator / Authy / MS Authenticator.
                  </div>
                </motion.div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="neu-btn neu-btn-primary w-full py-3.5 mt-2 text-[15px]"
                style={{ opacity: loading ? 0.7 : 1 }}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Autenticando...
                  </>
                ) : (
                  <>
                    {mfaRequired ? "Verificar acceso" : "Ingresar al sistema"}
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            {/* Trust footer inside card */}
            <div className="mt-8 pt-6 flex items-center justify-center gap-2 text-[11px]" style={{ borderTop: "1px solid #F1F5F9", color: "#94A3B8" }}>
              <ShieldCheck size={12} />
              <span>Conexión segura · Acceso restringido a personal autorizado</span>
            </div>
          </div>

          {/* Below card version */}
          <div className="mt-6 text-center text-[11px]" style={{ color: "#94A3B8" }}>
            <button
              onClick={() => setAboutOpen(true)}
              className="hover:underline"
              data-testid="login-version-badge"
            >
              Alfa-Field Connect v{APP_VERSION}
            </button>
          </div>
        </motion.div>
      </div>

      <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </div>
  );
}
